import React, { useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { FaCalculator } from 'react-icons/fa6';
const { types } = require('sharetribe-flex-sdk');
import { formatMoney } from '../../util/currency';
const { Money } = types;

import css from './PriceCalculator.module.css';
import ModalInMobile from '../ModalInMobile/ModalInMobile';
const MODAL_BREAKPOINT = 1023;

const PriceCalculator = props => {
  const { intl, price, cleaningFee, rentalType = [], monthlyPrice, weeklyPrice } = props;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [days, setDays] = useState('0');
  const [weeks, setWeeks] = useState('0');
  const [months, setMonths] = useState('1');

  const selectedRentalType = type => rentalType.includes(type);

  const toMoney = val => {
    if (!val && val !== 0) return null;
    if (val.amount !== undefined && val.currency) return val;
    if (typeof val === 'number' && props.marketplaceCurrency) {
      return new Money(val, props.marketplaceCurrency);
    }
    return null;
  };

  const handleOpenMobile = () => setIsMobileOpen(prev => !prev);

  const blockInvalidNumberKeys = e => {
    if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
      e.preventDefault();
    }
  };
const roundInt = n => Math.round(n);

// This function is useful to know which price the system will select as a reference using the priority monthly>weekly>daily.
const getSource = () => {
  if (selectedRentalType("monthly") && monthlyBaseAmt > 0) return "monthly";
  if (selectedRentalType("weekly") && weeklyBaseAmt > 0) return "weekly";
  if (selectedRentalType("daily") && dailyBaseAmt > 0) return "daily";

  if (monthlyBaseAmt > 0) return "monthly";
  if (weeklyBaseAmt > 0) return "weekly";
  if (dailyBaseAmt > 0) return "daily";
  return null;
};

const getCustomAmt = period => {
  if (period === "daily") return dailyBaseAmt;
  if (period === "weekly") return weeklyBaseAmt;
  if (period === "monthly") return monthlyBaseAmt;
  return 0;
};
const getConvertedValues = (period, quantity) => {
  const q = Number(quantity) || 0;
  if (q <= 0) return 0;

  // References
  const DAYS_PER_WEEK = 7;
  const DAYS_PER_MONTH = 30;

  // If the period is selected and is "daily," break it down into months+weeks+days.
  if (period === "daily") {
    const months = Math.floor(q / DAYS_PER_MONTH);
    const remainingAfterMonths = q % DAYS_PER_MONTH;
    const weeks = Math.floor(remainingAfterMonths / DAYS_PER_WEEK);
    const days = remainingAfterMonths % DAYS_PER_WEEK;

    const monthAmt = selectedRentalType("monthly") && monthlyBaseAmt > 0
      ? monthlyBaseAmt
      : selectedRentalType("weekly") && weeklyBaseAmt > 0
        ? weeklyBaseAmt * 4
        : dailyBaseAmt * DAYS_PER_MONTH;

    const weekAmt = selectedRentalType("weekly") && weeklyBaseAmt > 0
      ? weeklyBaseAmt
      : selectedRentalType("monthly") && monthlyBaseAmt > 0
        ? roundInt(monthlyBaseAmt / 4)
        : dailyBaseAmt * DAYS_PER_WEEK;

    const dayAmt = selectedRentalType("daily") && dailyBaseAmt > 0
      ? dailyBaseAmt
      : selectedRentalType("weekly") && weeklyBaseAmt > 0
        ? roundInt(weeklyBaseAmt / DAYS_PER_WEEK)
        : roundInt(monthlyBaseAmt / DAYS_PER_MONTH);

    return roundInt(months * monthAmt + weeks * weekAmt + days * dayAmt);
  }

  if (selectedRentalType(period)) {
    const custom = getCustomAmt(period);
    return custom > 0 ? custom * q : 0;
  }

  const source = getSource();
  if (!source) return 0;

  const DAYS_PER_WEEK_CALC = 30 / 4;
  const DAYS_PER_MONTH_CALC = 30;

  if (source === "daily") {
    if (period === "weekly")  return roundInt(dailyBaseAmt * DAYS_PER_WEEK_CALC * q);
    if (period === "monthly") return roundInt(dailyBaseAmt * DAYS_PER_MONTH_CALC * q);
  }

  if (source === "weekly") {
    if (period === "weekly")  return roundInt(weeklyBaseAmt * q);
    if (period === "monthly") return roundInt(weeklyBaseAmt * 4 * q);
  }

  if (source === "monthly") {
    if (period === "weekly")  return roundInt((monthlyBaseAmt / 4) * q);
    if (period === "monthly") return roundInt(monthlyBaseAmt * q);
  }

  return 0;
};

  const nonRefundableFee = 15000;
  const PARTNER_FEE_PERCENT = 5;
  const CUSTOMER_FEE_PERCENT = 10;

  const monthlyMoney = toMoney(monthlyPrice);
  const weeklyMoney = toMoney(weeklyPrice);
  const dailyMoney = toMoney(price);

  const monthlyBaseAmt = monthlyMoney ? monthlyMoney.amount : Number(monthlyPrice) || 0;
  const weeklyBaseAmt = weeklyMoney ? weeklyMoney.amount : Number(weeklyPrice) || 0;
  const dailyBaseAmt = dailyMoney ? dailyMoney.amount : Number(price) || 0;

  const cleaningAmt = toMoney(cleaningFee) ? toMoney(cleaningFee).amount : Number(cleaningFee) || 0;
  const nonRefundableAmt = toMoney(nonRefundableFee)
    ? toMoney(nonRefundableFee).amount
    : nonRefundableFee;

  // Parse string values — empty/0 count as "not set"
  const monthsNum = Number(months) || 0;
  const weeksNum = Number(weeks) || 0;
  const daysNum = Number(days) || 0;

  // A period is "active" only when its value is a positive number
  // const monthsActive = monthsNum > 0;
  // const weeksActive = weeksNum > 0;
  // const daysActive = daysNum > 0;

  const grandTotal =
    getConvertedValues("daily", daysNum) +
    getConvertedValues("weekly", weeksNum) +
    getConvertedValues("monthly", monthsNum);

  const currencyRef = monthlyMoney || weeklyMoney || dailyMoney;

  const formatAmt = amt => {
    if (!currencyRef) return amt;
    const safeAmt = Number.isFinite(Number(amt)) ? Math.round(Number(amt)) : 0;
    return formatMoney(intl, new Money(safeAmt, currencyRef.currency));
  };

  const renderCalculator = containerClass => (
    <div className={containerClass}>
      <FormattedMessage id="PriceCalculator.calculatorTitle" />

      {rentalType.length > 0 ? (
        <div className={css.calculator}>
          {/* Days input */}

            <div className={css.selectQuantityRow}>
              <span>{intl.formatMessage({ id: 'PriceCalculator.quantityLabel.days' })}</span>
              <input
                className={css.quantityInput}
                type="number"
                min="0"
                onKeyDown={blockInvalidNumberKeys}
                value={days}
                onChange={e => setDays(e.target.value)}
              />
            </div>

          {/* Weeks input */}
            <div className={css.selectQuantityRow}>
              <span>{intl.formatMessage({ id: 'PriceCalculator.quantityLabel.weeks' })}</span>
              <input
                className={css.quantityInput}
                type="number"
                min="0"
                onKeyDown={blockInvalidNumberKeys}
                value={weeks}
                onChange={e => setWeeks(e.target.value)}
              />
            </div>


          {/* Months input */}
            <div className={css.selectQuantityRow}>
              <span>{intl.formatMessage({ id: 'PriceCalculator.quantityLabel.months' })}</span>
              <input
                className={css.quantityInput}
                type="number"
                min="0"
                onKeyDown={blockInvalidNumberKeys}
                value={months}
                onChange={e => setMonths(e.target.value)}
              />
            </div>


          {/* Daily subtotal */}
            <div className={css.calculatorRow}>
              <span>
                {intl.formatMessage({ id: 'PriceCalculator.quantityLabel.days' })}{' '}
                {intl.formatMessage(
                  { id: 'PriceCalculator.basePrice' },
                  { quantity: daysNum || 0 }
                )}
              </span>
              <span>{daysNum > 0 ? formatAmt(getConvertedValues("daily", daysNum)) : "---"}</span>
            </div>

          {/* Weekly subtotal */}
            <div className={css.calculatorRow}>
              <span>
                {intl.formatMessage({ id: 'PriceCalculator.quantityLabel.weeks' })}{' '}
                {intl.formatMessage(
                  { id: 'PriceCalculator.basePrice' },
                  { quantity: weeksNum || 0 }
                )}
              </span>
            <span>{weeksNum > 0 ? formatAmt(getConvertedValues("weekly", weeksNum)) : "---"}</span>
            </div>

          {/* Monthly subtotal */}
            <div className={css.calculatorRow}>
              <span>
                {intl.formatMessage({ id: 'PriceCalculator.quantityLabel.months' })}{' '}
                {intl.formatMessage(
                  { id: 'PriceCalculator.basePrice' },
                  { quantity: monthsNum || 0 }
                )}
              </span>
            <span>{monthsNum > 0 ? formatAmt(getConvertedValues("monthly", monthsNum)) : "---"}</span>
            </div>

          {((monthsNum !== 0 && selectedRentalType('monthly'))|| (weeksNum !== 0 && selectedRentalType('weekly'))|| daysNum !== 0 && selectedRentalType('daily')) ? (
            <>

              <hr className={css.divider} />

              {currencyRef && cleaningFee && (
                <>
                  <div className={css.calculatorRow}>
                    <span>{intl.formatMessage({ id: 'PriceCalculator.total' })}</span>
                    <span>{formatAmt(grandTotal)}</span>
                  </div>

                  <div className={css.calculatorRow}>
                    <span>
                      {intl.formatMessage(
                        { id: 'PriceCalculator.partnerFee' },
                        { percent: PARTNER_FEE_PERCENT }
                      )}
                    </span>
                    <span>{formatAmt(grandTotal * (PARTNER_FEE_PERCENT / 100))}</span>
                  </div>

                  <div className={css.calculatorRow}>
                    <span>
                      {intl.formatMessage(
                        { id: 'PriceCalculator.customerFee' },
                        { percent: CUSTOMER_FEE_PERCENT }
                      )}
                    </span>
                    <span>{formatAmt(grandTotal * (CUSTOMER_FEE_PERCENT / 100))}</span>
                  </div>

                  <div className={css.calculatorRow}>
                    <span className={css.youEarn}>
                      {intl.formatMessage({ id: 'PriceCalculator.youEarn' })}
                    </span>
                    <span className={css.youEarn}>
                      {formatAmt(
                        grandTotal -
                          grandTotal * (PARTNER_FEE_PERCENT / 100) -
                          grandTotal * (CUSTOMER_FEE_PERCENT / 100)
                      )}
                    </span>
                  </div>
                </>
              )}
            </>
          ) :  <p className={css.noTabSelected}>
          {intl.formatMessage({ id: 'PriceCalculator.noResults' })}
        </p> }
        </div>
      ) : (
        <p className={css.noTabSelected}>
          {intl.formatMessage({ id: 'PriceCalculator.noTabSelected' })}
        </p>
      )}
    </div>
  );

  return (
    <>
      <button
        className={css.mobileButton}
        onClick={handleOpenMobile}
        title={intl.formatMessage({ id: 'PriceCalculator.openCalculator' })}
        aria-label={intl.formatMessage({ id: 'PriceCalculator.openCalculator' })}
      >
        <FaCalculator className={css.calculatorIcon} />
      </button>

      {renderCalculator(css.priceCalculatorContainer)}

      <div className={css.priceCalculatorContainerMobileWrapper}>
        <ModalInMobile
          id="price-calculator-ModalInMobile"
          containerClassName={css.modalContainer}
          isModalOpenOnMobile={isMobileOpen}
          onClose={() => setIsMobileOpen(false)}
          showAsModalMaxWidth={MODAL_BREAKPOINT}
          onManageDisableScrolling={props.onManageDisableScrolling || (() => {})}
          usePortal
        >
          {renderCalculator(css.priceCalculatorContainerMobile)}
        </ModalInMobile>
      </div>
    </>
  );
};

export default PriceCalculator;
